import { forwardRef, type AnchorHTMLAttributes, type ReactElement, type Ref } from 'react'
import { Button as BaseButton } from '@base-ui/react/button'
import { Link, type To } from 'react-router-dom'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip } from './Tooltip'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'inverse'
export type ButtonSize = 'default' | 'compact'

type ButtonStyle = {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Lifts a floating action above the content it sits on, such as a fixed feedback button, with the overlay
   *  shadow in every state. Translucent and ghost fills become opaque so the content below doesn't show through. */
  elevated?: boolean
}

/** Class names for a Play button. Prefer the components; this exists for elements they cannot render. */
export function buttonClassName({ variant = 'primary', size = 'default', elevated = false }: ButtonStyle = {}, className?: string) {
  return cn(
    'play-button',
    variant !== 'primary' && `play-button--${variant}`,
    size === 'compact' && 'play-button--compact',
    elevated && 'play-button--elevated',
    className,
  )
}

export type ButtonProps = Omit<BaseButton.Props, 'className'> & ButtonStyle & {
  className?: string
  /** Shows progress and blocks repeat activation while keeping the label and focus in place. */
  loading?: boolean
}

/** A labelled action. One primary button leads each view. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, elevated, loading = false, disabled = false, focusableWhenDisabled = false, className, children, ...props },
  ref,
) {
  return (
    <BaseButton
      ref={ref as Ref<HTMLElement>}
      disabled={disabled || loading}
      focusableWhenDisabled={focusableWhenDisabled || loading}
      aria-busy={loading || undefined}
      className={buttonClassName({ variant, size, elevated }, className)}
      {...props}
    >
      {loading && <LoaderCircle className="play-button__spinner" aria-hidden="true" />}
      {children}
    </BaseButton>
  )
})

export type IconButtonProps = Omit<ButtonProps, 'children' | 'aria-label'> & {
  /** The action name: read by assistive technology and shown as the tooltip. */
  label: string
  /** A Lucide icon. */
  children: ReactElement
  /** Set to false when a visible label nearby already names the action. */
  tooltip?: boolean
}

/** A familiar secondary action shown as an icon, with a persistent accessible name and tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, tooltip = true, variant = 'secondary', className, children, ...props },
  ref,
) {
  const button = (
    <Button ref={ref} variant={variant} aria-label={label} className={cn('play-button--icon', className)} {...props}>
      {children}
    </Button>
  )
  return tooltip ? <Tooltip content={label}>{button}</Tooltip> : button
})

type LinkTarget = { to: To; href?: never } | { href: string; to?: never }

export type ButtonLinkProps = ButtonStyle & LinkTarget & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  /** Replace the current history entry instead of adding one. Router links only. */
  replace?: boolean
}

/** Navigation styled as a button. Renders a real link: `to` for app routes, `href` for anything else. */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant, size, elevated, className, to, href, replace, ...props },
  ref,
) {
  const classes = buttonClassName({ variant, size, elevated }, className)
  if (to !== undefined) return <Link ref={ref} to={to} replace={replace} className={classes} {...props} />
  return <a ref={ref} href={href} className={classes} {...props} />
})

export type IconButtonLinkProps = ButtonStyle & LinkTarget & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children' | 'aria-label'> & {
  replace?: boolean
  /** The destination name: read by assistive technology and shown as the tooltip. */
  label: string
  /** A Lucide icon. */
  children: ReactElement
  tooltip?: boolean
}

/** Icon-only navigation: a real link with a required name and tooltip, styled like IconButton. */
export const IconButtonLink = forwardRef<HTMLAnchorElement, IconButtonLinkProps>(function IconButtonLink(
  { label, tooltip = true, variant = 'secondary', className, children, ...props },
  ref,
) {
  const link = (
    <ButtonLink ref={ref} variant={variant} aria-label={label} className={cn('play-button--icon', className)} {...props}>
      {children}
    </ButtonLink>
  )
  return tooltip ? <Tooltip content={label}>{link}</Tooltip> : link
})
